// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle
import { ISetupStepProps } from ".";
import styled from "@emotion/styled";
import { filter, pipe } from "callbag-basics";
import subscribe from "callbag-subscribe";
import { useCallback, useEffect, useState } from "react";
import * as React from "react";

import { Button, DesignToken, FormField, Input, SpacingToken, Text } from "@livechat/design-system-react-components";

import { ButtonGroup } from "components/button/ButtonGroup";
import Block from "components/layout/Block";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { actions$ } from "store/action-stream";
import { CRUDAction, RequestAction } from "store/entities/actions";
import { EMAIL_DOMAIN, ICreateEmailDomainFailurePayload } from "store/entities/email-domains";
import { IAction, IActionWithPayload } from "store/helper";
import { withVar } from "themes/helpers";

import { SetupHelpdeskDomainAction } from "../DomainSetupModal";

function validate(name: string): string | null {
  if (name.length > 63) {
    return "Domain name must be shorter than 64 characters.";
  }

  if (name.length < 2) {
    return "Domain name must be longer than 1 character.";
  }

  if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(name)) {
    return name.startsWith("-") || name.endsWith("-")
      ? "Domain name cannot start or end with „-”."
      : "Invalid domain name. Use only letters, digits, and „-”.";
  }

  return null;
}

const SetupHelpdeskDomain: React.FC<ISetupStepProps<SetupHelpdeskDomainAction>> = ({
  setDomainName,
  domains,
  domainName,
  createCustomDomain,
}) => {
  const [validationError, setValidationError] = useState<string | null>(null);
  const handleDomainNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setDomainName(event.target.value.toLowerCase());
      setValidationError(null);
    },
    [setDomainName, setValidationError]
  );

  useEffect(() => {
    const unsubscribe = pipe(
      actions$,
      filter(
        (action: IActionWithPayload<string, ICreateEmailDomainFailurePayload>) =>
          action.type === EMAIL_DOMAIN.CREATE[RequestAction.FAILURE]
      ),
      subscribe((action: IActionWithPayload<string, ICreateEmailDomainFailurePayload>) => {
        const { payload } = action;

        if (payload.errorObject && payload.errorObject.type === "conflict") {
          setValidationError("This domain name is already used. Please choose another domain name.");
          trackEngagement("Settings: Domain creation failed", EventPlace.Settings, {
            domainName,
            error: "Domain is already used on other license",
            type: "helpdesk",
          });
        } else {
          setValidationError("Domain has not been created. Contact support.");
          trackEngagement("Settings: Domain creation failed", EventPlace.Settings, {
            domainName,
            error: "Generic error",
            type: "helpdesk",
          });
        }
      })
    );

    return unsubscribe;
  });

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleHelpdeskDomainSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const existingDomain = domains.find((domain: any) => domain.name?.toLowerCase() === domainName?.toLowerCase());
    const domainNameValidation = validate(domainName);

    if (existingDomain) {
      trackEngagement("Settings: Domain creation failed", EventPlace.Settings, {
        domainName,
        error: "Domain is already defined",
        type: "helpdesk",
      });
      setValidationError("This domain name is already used. Please choose another domain name.");
    } else if (domainNameValidation) {
      trackEngagement("Settings: Domain creation failed", EventPlace.Settings, {
        domainName,
        error: domainNameValidation,
        type: "helpdesk",
      });
      setValidationError(domainNameValidation);
    } else {
      createCustomDomain(domainName);
    }
  };

  return (
    <>
      <Text customColor={withVar(DesignToken.ContentBasicSecondary)}>
        Enter your HelpDesk domain name. It must be 3-63 characters long and contain only letters, digits, and dashes.
      </Text>
      <form id="domain-name-form" onSubmit={handleHelpdeskDomainSubmit}>
        <Block marginV={withVar(SpacingToken.Spacing4)}>
          <DomainFormField labelText="Domain name" error={validationError ?? undefined}>
            <DomainInputField
              placeholder="yourdomain"
              id="domain-name"
              name="domain-name"
              value={domainName}
              error={!!validationError}
              onChange={handleDomainNameChange}
            />
          </DomainFormField>
        </Block>
      </form>
    </>
  );
};

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const handleLoading = (fn: (action: IAction) => void) =>
  pipe(
    actions$,
    filter((action: IAction) => {
      const actions = [
        EMAIL_DOMAIN[CRUDAction.CREATE][RequestAction.REQUEST],
        EMAIL_DOMAIN[CRUDAction.CREATE][RequestAction.SUCCESS],
        EMAIL_DOMAIN[CRUDAction.CREATE][RequestAction.FAILURE],
      ];

      return actions.includes(action.type);
    }),
    subscribe(fn)
  );

const SetupHelpdeskDomainButtons: React.FC<ISetupStepProps<SetupHelpdeskDomainAction>> = ({
  onClose,
  domainName,
  showCreateEmailDomainSuccessToast,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const handleClose = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      onClose();
    },
    [onClose]
  );

  useEffect(() => {
    const unsubscribe = handleLoading(action => {
      switch (action.type) {
        case EMAIL_DOMAIN[CRUDAction.CREATE][RequestAction.REQUEST]:
          setLoading(true);
          return;
        case EMAIL_DOMAIN[CRUDAction.CREATE][RequestAction.SUCCESS]:
          onClose();
          showCreateEmailDomainSuccessToast();
          return;
        case EMAIL_DOMAIN[CRUDAction.CREATE][RequestAction.FAILURE]:
          setLoading(false);
          return;
        default:
          setLoading(false);
      }
    });

    return unsubscribe;
  }, [onClose, setLoading, showCreateEmailDomainSuccessToast]);

  return (
    <ButtonGroup>
      <Button kind="secondary" onClick={handleClose}>
        Cancel
      </Button>
      <Button kind="primary" type="submit" loading={loading} form="domain-name-form" disabled={!domainName}>
        Add domain
      </Button>
    </ButtonGroup>
  );
};

export { SetupHelpdeskDomain, SetupHelpdeskDomainButtons };

const DomainInputField = styled(Input)`
  width: 100%;
`;

const DomainFormField = styled(FormField)`
  width: 100%;
  position: relative;

  ::after {
    content: ".helpdesk.com";
    position: absolute;
    right: var(${SpacingToken.Spacing3});
    top: var(${SpacingToken.Spacing9});
    color: var(${DesignToken.ContentBasicSecondary});
  }

  input {
    padding-right: var(${SpacingToken.Spacing24});
  }
`;
